import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, User, Menu, X } from 'lucide-react';
import HomeCrousal from './HomeCrousal';
import NewFooter from '../../common/NewFooter';
import FlowChart from './FlowChart';
import ContactForm from './ContactForm';
import GoogleReviews from '../../common/GoogleReviews';
import researchLogo from '../../../assets/images/logos/Assurance/ResearchLens.jpeg';
import MIan from './Sidebar/MIan';


const navigationItems = [
  { name: 'Home', to: 'home' },
  { name: 'Our Services', to: 'services' },
  { name: 'Research', to: 'research' },
  { name: 'Contact Us', to: 'contact' },
  { name: 'Back to Home', to: '/', external: true }
];

const Navigation = () => {
  const [activeSection, setActiveSection] = useState('Home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const sectionRefs = useRef({});

  useEffect(() => {

    navigationItems.forEach(item => {
      if (!item.external) {
        sectionRefs.current[item.to] = document.getElementById(item.to);
      }
    });

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrolled(currentScrollY > 0);

      // Auto-hide mobile menu on scroll down
      if (isMobileMenuOpen && currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsMobileMenuOpen(false);
      }

      setLastScrollY(currentScrollY);

      if (currentScrollY < 100) {
        setActiveSection('Home');
        return;
      }

      let currentSection = '';
      const scrollPosition = currentScrollY + 150;

      for (const item of navigationItems) {
        if (item.external) continue;

        const section = sectionRefs.current[item.to];
        if (!section) continue;

        const sectionTop = section.offsetTop;
        const sectionBottom = sectionTop + section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
          currentSection = item.name;
          break;
        }
      }

      if (currentSection && activeSection !== currentSection) {
        setActiveSection(currentSection);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection, isMobileMenuOpen, lastScrollY]);

  const orangeStyles = {
    primary: '#FF6B35',
    light: '#FFE5DB',
    dark: '#E55A2B',
    hover: '#FF7A47'
  };

  const handleNavClick = (item) => {
    if (item.external) {
      window.open(item.to, '_blank');
      return;
    }

    const targetElement = document.getElementById(item.to);
    if (!targetElement) {
      console.warn(`Section with id "${item.to}" not found`);
      return;
    }

    const navHeight = 120;
    const targetPosition = targetElement.offsetTop - navHeight;

    setActiveSection(item.name);

    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: 'smooth'
    });

    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'shadow-lg' : ''
        }`}
        style={{
          transform: isScrolled ? 'translateY(-36px)' : 'translateY(0)',
          backgroundColor: 'white'
        }}>

        <div style={{ backgroundColor: '#334155', color: 'white', padding: '8px 16px', height: '36px' }}>
          <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">aeggrouphelpdesk@gmail.com</span>
                <span className="sm:hidden">Contact Us</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>+91 6268558700</span>

              </div>
            </div>
            <div
              className="flex items-center space-x-2 cursor-pointer transition-colors duration-200"
              style={{ color: 'white' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = orangeStyles.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'white')}
              onClick={() => window.location.href = "https://crm.aegservices.in/admin/"}
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Employee Login</span>
              <span className="sm:hidden">Login</span>
            </div>

          </div>
        </div>

        <div style={{ backgroundColor: 'white', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-1">
              <img
                src={researchLogo}
                alt="Assurance Education Group Logo"
                className="w-24 h-24 rounded-full object-contain"
              />
              <div>
                <div className="flex items-baseline space-x-2">
                  <span className="font-bold text-2xl" style={{ color: orangeStyles.primary }}>Research</span>
                  <span className="text-gray-700 text-2xl font-semibold px-1">Lens</span>
                  {/* <span className="font-bold text-xl" style={{color: orangeStyles.primary}}>group</span> */}
                </div>
                {/* <p className="text-gray-600 text-sm -mt-1">All solutions under one roof</p> */}
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-2">
              {navigationItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item)}
                  className="px-4 py-2 text-sm font-medium transition-all duration-200 relative rounded-md"
                  style={activeSection === item.name ?
                    { backgroundColor: orangeStyles.primary, color: 'white' } :
                    { color: '#374151', backgroundColor: 'transparent' }
                  }
                  onMouseEnter={(e) => {
                    if (activeSection !== item.name) {
                      e.currentTarget.style.backgroundColor = orangeStyles.light;
                      e.currentTarget.style.color = orangeStyles.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSection !== item.name) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#374151';
                    }
                  }}
                >
                  {item.name}
                  {activeSection === item.name && (
                    <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full"
                      style={{ backgroundColor: orangeStyles.primary }}></div>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md transition-colors duration-200"
              style={{ color: '#374151' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = orangeStyles.primary;
                e.currentTarget.style.backgroundColor = orangeStyles.light;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu Dropdown with compact width */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute right-4 mt-4 z-40"
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)',
                minWidth: '200px',
                width: 'fit-content'
              }}>
              {/* Compact container - only takes needed space */}
              <div className="px-2 py-3">
                <div className="flex flex-col space-y-1">
                  {navigationItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => {
                        handleNavClick(item);
                        setIsMobileMenuOpen(false);
                      }}
                      className="px-4 py-3 text-left text-sm font-medium transition-all duration-200 rounded-md whitespace-nowrap text-left w-full"
                      style={activeSection === item.name ?
                        {
                          color: orangeStyles.primary,
                          backgroundColor: orangeStyles.light,
                          borderLeft: `4px solid ${orangeStyles.primary}`
                        } :
                        { color: '#374151', backgroundColor: 'transparent' }
                      }
                      onMouseEnter={(e) => {
                        if (activeSection !== item.name) {
                          e.currentTarget.style.color = orangeStyles.primary;
                          e.currentTarget.style.backgroundColor = orangeStyles.light;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeSection !== item.name) {
                          e.currentTarget.style.color = '#374151';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div id="home" className="pt-32">
        <HomeCrousal />
      </div>

      <section className="mx-auto max-w-5xl px-4 py-12 text-center">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
          Best Thesis Writing Services in Bhopal
        </h1>
        <p className="text-gray-700 leading-relaxed">
          Assurance Education Group (ResearchLens / aegresearch) provides the best thesis
          writing services in Bhopal for PhD and M.Tech scholars. Our experts deliver
          end-to-end thesis writing, paper writing, SCI research paper preparation,
          research paper publication, SCI paper publication and Scopus paper publication
          support &mdash; covering topic selection, research methodology, implementation in
          MATLAB, ANSYS and Python, plagiarism-free writing, editing and journal submission.
          Call <a href="tel:+916268558700" className="text-blue-700 font-semibold">+91 6268558700</a>.
        </p>
      </section>

      <div id="services" className="min-h-screen w-full flex items-center justify-center pt-20">
        {/* <ResearchHubPage/> */}
        <MIan />
      </div>

      <div id="research" className="min-h-screen w-full flex items-center justify-center overflow-x-hidden pt-20">
        <FlowChart />
      </div>

      <GoogleReviews />

      <div id="contact" className="min-h-screen w-full flex items-center justify-center pt-20">
        <ContactForm />
      </div>
      <NewFooter />

    </>
  );
};

export default Navigation;